import { useEffect, useState } from "react";
import { collection, getDocs, orderBy, query, deleteDoc, doc } from "firebase/firestore";
import { firestore } from "../../firebase";
import { IconSearch, IconTrash } from "@tabler/icons-react";
import { toast } from "sonner";

type ContactForm = {
	id: string;
	createdAt: string;
	description: string;
	email: string;
	nameSurname: string;
	subject: string;
};

export default function ContactFormsPage() {
	const [contacts, setContacts] = useState<ContactForm[]>([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");

	const fetchContacts = async () => {
		try {
			const contactsQuery = query(collection(firestore, "contactForm"), orderBy("createdAt", "desc"));
			const querySnapshot = await getDocs(contactsQuery);
			const contactsData = querySnapshot.docs.map((doc) => ({
				id: doc.id,
				...doc.data(),
			})) as ContactForm[];
			setContacts(contactsData);
		} catch (error) {
			console.error("Error fetching contacts:", error);
			toast.error("Failed to load contact forms");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchContacts();
	}, []);

	const handleDelete = async (id: string) => {
		try {
			await deleteDoc(doc(firestore, "contactForm", id));
			toast.success("Contact form deleted successfully");
			fetchContacts();
		} catch (error) {
			console.error("Error deleting contact:", error);
			toast.error("Failed to delete contact form");
		}
	};

	const filteredContacts = contacts.filter(
		(contact) =>
			contact.nameSurname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
			contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
			contact.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
			contact.subject?.toLowerCase().includes(searchTerm.toLowerCase())
	);

	if (loading) {
		return (
			<div className='flex h-[calc(100vh-4rem)] items-center justify-center'>
				<div className='h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent'></div>
			</div>
		);
	}

	return (
		<div>
			<div className='mb-8 sm:flex sm:items-center sm:justify-between'>
				<div>
					<h1 className='text-2xl font-semibold text-gray-900 dark:text-white'>Contact Forms</h1>
					<p className='mt-2 text-sm text-gray-700 dark:text-gray-400'>
						A list of all contact form submissions ({contacts.length})
					</p>
				</div>
				<div className='mt-4 sm:mt-0'>
					<div className='relative'>
						<div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
							<IconSearch className='h-5 w-5 text-gray-400' />
						</div>
						<input
							type='text'
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							placeholder='Search contacts...'
							className='block w-full rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 py-2.5 pl-10 pr-3 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:focus:border-primary'
						/>
					</div>
				</div>
			</div>

			<div className='mt-4 flow-root'>
				<div className='-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8'>
					<div className='inline-block min-w-full py-2 align-middle'>
						<div className='overflow-hidden shadow ring-1 ring-black ring-opacity-5 dark:ring-white/10 sm:rounded-lg'>
							<table className='min-w-full divide-y divide-gray-200 dark:divide-gray-800'>
								<thead className='bg-gray-50 dark:bg-gray-800/50'>
									<tr>
										<th className='py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-900 dark:text-gray-200 sm:pl-6'>
											Name
										</th>
										<th className='px-3 py-3.5 text-left text-sm font-medium text-gray-900 dark:text-gray-200'>
											Email
										</th>
										<th className='px-3 py-3.5 text-left text-sm font-medium text-gray-900 dark:text-gray-200'>
											Subject
										</th>
										<th className='px-3 py-3.5 text-left text-sm font-medium text-gray-900 dark:text-gray-200'>
											Description
										</th>
										<th className='px-3 py-3.5 text-left text-sm font-medium text-gray-900 dark:text-gray-200'>Date</th>
										<th className='relative py-3.5 pl-3 pr-4 sm:pr-6'>
											<span className='sr-only'>Actions</span>
										</th>
									</tr>
								</thead>
								<tbody className='divide-y divide-gray-200 dark:divide-gray-800 bg-white dark:bg-gray-900'>
									{filteredContacts.map((contact) => (
										<tr key={contact.id} className='hover:bg-gray-50 dark:hover:bg-gray-800/50'>
											<td className='whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 dark:text-gray-100 sm:pl-6'>
												{contact.nameSurname}
											</td>
											<td className='whitespace-nowrap px-3 py-4 text-sm text-gray-600 dark:text-gray-400'>
												{contact.email}
											</td>
											<td className='whitespace-nowrap px-3 py-4 text-sm text-gray-600 dark:text-gray-400'>
												{contact.subject}
											</td>
											<td className='max-w-md truncate px-3 py-4 text-sm text-gray-600 dark:text-gray-400'>
												{contact.description}
											</td>
											<td className='whitespace-nowrap px-3 py-4 text-sm text-gray-600 dark:text-gray-400'>
												{new Date(contact.createdAt).toLocaleString()}
											</td>
											<td className='relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6'>
												<button
													onClick={() => handleDelete(contact.id)}
													className='text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300'>
													<IconTrash className='h-5 w-5' />
													<span className='sr-only'>Delete</span>
												</button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
